import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { del, get, post } from "../api";
import { PaginatedList } from "../components/PaginatedList";
import { useI18n } from "../i18n";

interface Detail {
  id: string; name: string; members: number; sites: number;
  subscription: { plan_code: string; service_state: string } | null;
  usage: {
    period_key: string; granted_au: number; pv_reserved_au: number;
    pv_used: number; origin_used: number; custom_used: number;
  } | null;
  capabilities: string[];
}

interface SiteRow {
  id: string; name: string; mode: string; status: string;
  collection_state: string;
}

interface MemberRow {
  user_id: string; email: string; capabilities: string[]; status: string;
}

export function WorkspaceDetailPage() {
  const { t, locale } = useI18n();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Owner transfer & delete workspace modals
  const [transferTarget, setTransferTarget] = useState<MemberRow | null>(null);
  const [reauthPassword, setReauthPassword] = useState("");
  const [transferErr, setTransferErr] = useState<string | null>(null);
  const [showDeleteWsModal, setShowDeleteWsModal] = useState(false);
  const [deleteWsErr, setDeleteWsErr] = useState<string | null>(null);
  const [showCancelSubModal, setShowCancelSubModal] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [subErr, setSubErr] = useState<string | null>(null);
  const [siteToDelete, setSiteToDelete] = useState<SiteRow | null>(null);
  const [deleteSiteBusy, setDeleteSiteBusy] = useState(false);
  const [deleteSiteErr, setDeleteSiteErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const [detail, siteList, memberList] = await Promise.all([
      get<Detail>(`/api/v1/workspaces/${workspaceId}`),
      get<{ items: SiteRow[] }>(`/api/v1/workspaces/${workspaceId}/sites`),
      get<{ members: MemberRow[] }>(`/api/v1/workspaces/${workspaceId}/members`),
    ]);
    setD(detail); setSites(siteList.items); setMembers(memberList.members);
  }, [workspaceId]);

  useEffect(() => { load().catch((e) => setErr(e.message)); }, [load]);

  if (!d) return <div className="muted">{t.common.loading}</div>;
  const used = d.usage ? d.usage.pv_used + d.usage.origin_used + d.usage.custom_used : 0;
  const canManage = d.capabilities.includes("owner") || d.capabilities.includes("manage_members");

  return (
    <div>
      <div className="site-breadcrumbs">
        <Link to="/" className="site-breadcrumb-link">{t.common.workspaces}</Link>
        <span className="site-breadcrumb-sep">/</span>
        <span className="site-breadcrumb-current">{d.name}</span>
      </div>

      <div className="site-title-bar" style={{ marginBottom: 20 }}>
        <div>
          <h1 className="site-main-title">{d.name}</h1>
          <p className="site-subtitle">
            {d.subscription?.plan_code === "unlimited"
              ? (locale === "zh" ? "永久授权工作空间 · 无限额度" : "Perpetual License · Unlimited Capacity")
              : (locale === "zh"
                  ? `计划: ${d.subscription?.plan_code.toUpperCase() || "FREE"} · ${sites.length} 站点 · ${d.members} 成员`
                  : `Plan: ${d.subscription?.plan_code.toUpperCase() || "FREE"} · ${sites.length} sites · ${d.members} members`)}
          </p>
        </div>
      </div>

      {err && <p className="error">{err}</p>}

      <SetupWizard workspaceId={workspaceId ?? ""} onChanged={load}
                   canSetup={d.capabilities.includes("owner")
                              || d.capabilities.includes("manage_sites")} />

      {/* AU Usage Stats Grid */}
      {d.usage && (
        <div className="oc-stats-grid">
          <div className="oc-stat-card">
            <div className="oc-stat-label">
              <span>{t.workspaceDetail.usedAu}</span>
              <span className="badge">{d.usage.period_key}</span>
            </div>
            <div className="oc-stat-value" style={{ color: "var(--brand)" }}>
              {used.toLocaleString()}
            </div>
            <div className="oc-stat-footer">
              PV: {d.usage.pv_used.toLocaleString()} · Origin: {d.usage.origin_used.toLocaleString()}
            </div>
          </div>

          <div className="oc-stat-card">
            <div className="oc-stat-label">
              <span>{t.workspaceDetail.remainingAu}</span>
              <span className="badge ok">
                {d.subscription?.plan_code === "unlimited" ? "UNLIMITED" : "ACTIVE"}
              </span>
            </div>
            <div className="oc-stat-value" style={{ color: "var(--ok)" }}>
              {d.subscription?.plan_code === "unlimited" || d.usage.granted_au >= 999999999999
                ? (locale === "zh" ? "无限 (Unlimited)" : "Unlimited")
                : (d.usage.granted_au - used).toLocaleString()}
            </div>
            <div className="oc-stat-footer">
              {d.subscription?.plan_code === "unlimited"
                ? (locale === "zh" ? "✨ 永久授权不过期" : "✨ Never expires")
                : `${t.workspaceDetail.pvReserved}: ${d.usage.pv_reserved_au.toLocaleString()}`}
            </div>
          </div>

          <div className="oc-stat-card">
            <div className="oc-stat-label">
              <span>{t.workspaceDetail.sitesSection}</span>
            </div>
            <div className="oc-stat-value">
              {sites.length}
            </div>
            <div className="oc-stat-footer">
              {sites.filter(s => s.status === "active").length} {locale === "zh" ? "活跃运行中" : "active"}
            </div>
          </div>

          <div className="oc-stat-card">
            <div className="oc-stat-label">
              <span>{t.workspaceDetail.membersSection}</span>
            </div>
            <div className="oc-stat-value">
              {d.members}
            </div>
            <div className="oc-stat-footer">
              {d.capabilities.join(", ")}
            </div>
          </div>
        </div>
      )}

      {/* Subscription banner */}
      {d.subscription && (
        <div className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "14px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span>{t.workspaceDetail.planLabel} <b>{d.subscription.plan_code.toUpperCase()}</b></span>
            <span>·</span>
            <span>{t.workspaceDetail.statusLabel} <span className="badge ok">{d.subscription.service_state}</span></span>
            {d.subscription.plan_code === "unlimited" && (
              <span className="badge ok" style={{ background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" }}>
                {locale === "zh" ? "✨ 永久无限 · 不过期" : "✨ Unlimited · Never Expires"}
              </span>
            )}
          </div>
          {d.capabilities.includes("owner") && d.subscription.plan_code !== "free" && d.subscription.plan_code !== "unlimited" && d.subscription.service_state !== "canceled" && (
            <button
              className="ghost"
              style={{ color: "var(--danger)", borderColor: "#fca5a5", fontSize: 13, padding: "4px 10px" }}
              onClick={() => {
                setShowCancelSubModal(true);
                setSubErr(null);
              }}
            >
              {t.workspaceDetail.cancelSubscriptionBtn}
            </button>
          )}
        </div>
      )}

      {/* Sites List Card with PaginatedList */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t.workspaceDetail.sitesSection} ({sites.length})</h3>
        </div>
        {sites.length === 0 ? (
          <p className="muted">{t.workspaceDetail.noSites}</p>
        ) : (
          <PaginatedList
            items={sites}
            searchKey={(s) => `${s.name} ${s.mode} ${s.status}`}
            renderItem={(s) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 4px", borderBottom: "1px solid var(--line-light)" }}>
                <div>
                  <Link to={`/w/${workspaceId}/sites/${s.id}`} style={{ fontSize: 14, fontWeight: 600 }}>
                    {s.name}
                  </Link>
                  <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>({s.mode})</span>
                  <Link
                    to={`/w/${workspaceId}/sites/${s.id}?tab=sources`}
                    style={{
                      marginLeft: 10,
                      fontSize: 12,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "var(--brand-subtle)",
                      color: "var(--brand)",
                      border: "1px solid #bfdbfe",
                      textDecoration: "none",
                    }}
                    title={locale === "zh" ? "进入数据来源配置与密钥管理" : "Data Sources & Ingestion Setup"}
                  >
                    🔌 {locale === "zh" ? "数据来源 (Sources)" : "Sources"}
                  </Link>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span className={`badge ${s.status === "active" ? "ok" : "warn"}`}>{s.status}</span>
                  {(d.capabilities.includes("owner") || d.capabilities.includes("manage_sites")) && (
                    <button
                      className="ghost"
                      style={{ color: "var(--danger)", borderColor: "#fca5a5", fontSize: 12, padding: "2px 8px" }}
                      onClick={() => {
                        setSiteToDelete(s);
                        setDeleteSiteErr(null);
                      }}
                    >
                      {t.workspaceDetail.deleteSiteBtn}
                    </button>
                  )}
                </div>
              </div>
            )}
          />
        )}
      </div>

      {/* Members Section with PaginatedList */}
      <div className="card">
        <h3>{t.workspaceDetail.membersSection} ({d.members})</h3>
        <PaginatedList
          items={members}
          searchKey={(m) => `${m.email} ${m.capabilities.join(" ")} ${m.status}`}
          renderItem={(m) => (
            <div key={m.user_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 4px", borderBottom: "1px solid var(--line-light)" }}>
              <div>
                <b style={{ fontSize: 14 }}>{m.email}</b>
                <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>({m.capabilities.join(", ")})</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="badge">{m.status}</span>
                {canManage && !m.capabilities.includes("owner") && (
                  <div style={{ display: "flex", gap: 6 }}>
                    {d.capabilities.includes("owner") && (
                      <button className="ghost" style={{ color: "#d97706", fontSize: 12 }} onClick={() => {
                        setTransferTarget(m);
                        setReauthPassword("");
                        setTransferErr(null);
                      }}>{t.workspaceDetail.transferOwnership}</button>
                    )}
                    <button className="ghost" style={{ fontSize: 12 }} onClick={async () => {
                      await del(`/api/v1/workspaces/${workspaceId}/members/${m.user_id}`);
                      load();
                    }}>{t.workspaceDetail.removeMember}</button>
                  </div>
                )}
              </div>
            </div>
          )}
        />
        {canManage && (
          <div className="row" style={{ marginTop: 14 }}>
            <input value={inviteEmail} placeholder={t.workspaceDetail.memberEmailPlaceholder}
                   onChange={(e) => setInviteEmail(e.target.value)} />
            <button style={{ maxWidth: 140 }} onClick={async () => {
              setMsg(null); setErr(null);
              try {
                await post(`/api/v1/workspaces/${workspaceId}/members`,
                           { email: inviteEmail, capabilities: ["viewer"] });
                setMsg(t.workspaceDetail.inviteCreated);
                setInviteEmail("");
                load();
              } catch (e) { setErr((e as Error).message); }
            }}>{t.workspaceDetail.inviteViewer}</button>
          </div>
        )}
        {msg && <p className="muted" style={{ marginTop: 8 }}>{msg}</p>}
        {err && <p className="error">{err}</p>}
      </div>

      {/* Danger Zone for Owner */}
      {d.capabilities.includes("owner") && (
        <div className="card" style={{ borderColor: "#fca5a5", marginTop: 16 }}>
          <h3 style={{ color: "#b91c1c" }}>{t.workspaceDetail.dangerZone}</h3>
          <p className="muted">{t.workspaceDetail.dissolveNotice}</p>
          <button style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                  onClick={() => {
                    setShowDeleteWsModal(true);
                    setReauthPassword("");
                    setDeleteWsErr(null);
                  }}>
            {t.workspaceDetail.dissolveWorkspace}
          </button>
        </div>
      )}

      {/* Transfer Owner Modal */}
      {transferTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 440, width: "90%", background: "#fff" }}>
            <h3>{t.workspaceDetail.transferConfirmModalTitle}</h3>
            <p>{t.workspaceDetail.transferPrompt}<b>{transferTarget.email}</b>?{t.workspaceDetail.transferDemoteWarning}</p>
            {transferErr && <p className="error">{transferErr}</p>}
            <label>{t.workspaceDetail.reauthPasswordPlaceholder}:</label>
            <input type="password" value={reauthPassword}
                   onChange={(e) => setReauthPassword(e.target.value)}
                   placeholder={t.auth.passwordLabel} autoFocus />
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button className="primary" onClick={async () => {
                try {
                  await post("/api/v1/auth/reauth", { password: reauthPassword });
                  await post(`/api/v1/workspaces/${workspaceId}/transfer-owner`, {
                    target_user_id: transferTarget.user_id,
                  });
                  setTransferTarget(null);
                  setMsg(`${t.workspaceDetail.transferButton}: ${transferTarget.email}`);
                  load();
                } catch (e) {
                  setTransferErr((e as Error).message);
                }
              }}>{t.common.submit}</button>
              <button className="ghost" onClick={() => setTransferTarget(null)}>{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Workspace Modal */}
      {showDeleteWsModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 440, width: "90%", background: "#fff" }}>
            <h3 style={{ color: "#b91c1c" }}>{t.workspaceDetail.deleteConfirmModalTitle}</h3>
            <p className="error">{t.workspaceDetail.deleteWarningModal}</p>
            {deleteWsErr && <p className="error">{deleteWsErr}</p>}
            <label>{t.workspaceDetail.reauthPasswordPlaceholder}:</label>
            <input type="password" value={reauthPassword}
                   onChange={(e) => setReauthPassword(e.target.value)}
                   placeholder={t.auth.passwordLabel} autoFocus />
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                      onClick={async () => {
                        try {
                          await post("/api/v1/auth/reauth", { password: reauthPassword });
                          await del(`/api/v1/workspaces/${workspaceId}`);
                          window.location.href = "/";
                        } catch (e) {
                          setDeleteWsErr((e as Error).message);
                        }
                      }}>{t.workspaceDetail.confirmDissolveBtn}</button>
              <button className="ghost" onClick={() => setShowDeleteWsModal(false)}>{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelSubModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 440, width: "90%", background: "#fff" }}>
            <h3 style={{ color: "#b91c1c" }}>{t.workspaceDetail.cancelSubscriptionConfirmTitle}</h3>
            <p className="muted">{t.workspaceDetail.cancelSubscriptionConfirmPrompt}</p>
            {subErr && <p className="error">{subErr}</p>}
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button
                style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                disabled={subBusy}
                onClick={async () => {
                  setSubBusy(true);
                  setSubErr(null);
                  try {
                    await post(`/api/v1/workspaces/${workspaceId}/subscription/cancel`, {});
                    setShowCancelSubModal(false);
                    setMsg(t.workspaceDetail.cancelSubscriptionSuccess);
                    load();
                  } catch (e) {
                    setSubErr((e as Error).message);
                  } finally {
                    setSubBusy(false);
                  }
                }}
              >
                {subBusy ? t.common.submitting : t.common.submit}
              </button>
              <button className="ghost" onClick={() => setShowCancelSubModal(false)}>{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Site Modal */}
      {siteToDelete && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 440, width: "90%", background: "#fff" }}>
            <h3 style={{ color: "#b91c1c" }}>{t.workspaceDetail.deleteSiteConfirmTitle}: {siteToDelete.name}</h3>
            <p className="muted">{t.workspaceDetail.deleteSiteConfirmPrompt}</p>
            {deleteSiteErr && <p className="error">{deleteSiteErr}</p>}
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button
                style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                disabled={deleteSiteBusy}
                onClick={async () => {
                  setDeleteSiteBusy(true);
                  setDeleteSiteErr(null);
                  try {
                    await del(`/api/v1/sites/${siteToDelete.id}`);
                    setSiteToDelete(null);
                    setMsg(t.workspaceDetail.deleteSiteSuccess);
                    load();
                  } catch (e) {
                    setDeleteSiteErr((e as Error).message);
                  } finally {
                    setDeleteSiteBusy(false);
                  }
                }}
              >
                {deleteSiteBusy ? t.common.submitting : t.common.delete}
              </button>
              <button className="ghost" onClick={() => setSiteToDelete(null)}>{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DomainChallenge {
  hostname: string; txt_name: string; txt_value: string;
}

function SetupWizard({ workspaceId, onChanged, canSetup }: {
  workspaceId: string; onChanged: () => void; canSetup: boolean;
}) {
  const { t, locale } = useI18n();
  const [siteName, setSiteName] = useState("");
  const [siteId, setSiteId] = useState<string | null>(null);
  const [hostname, setHostname] = useState("");
  const [challenge, setChallenge] = useState<DomainChallenge | null>(null);
  const [pub, setPub] = useState<string | null>(null);
  const [agent, setAgent] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!canSetup) return null;

  const call = async (fn: () => Promise<void>) => {
    setErr(null); setMsg(null);
    try { await fn(); } catch (e) { setErr((e as Error).message); }
  };

  return (
    <div className="card">
      <h3>{t.workspaceDetail.setupWizardTitle}</h3>
      <div className="row">
        <input placeholder={t.workspaceDetail.siteNamePlaceholder} value={siteName}
               onChange={(e) => setSiteName(e.target.value)} />
        <button onClick={() => call(async () => {
          const r = await post<{ id: string }>(`/api/v1/workspaces/${workspaceId}/sites`,
            { name: siteName || "Site", mode: "hybrid" });
          setSiteId(r.id); setMsg("Site created (draft)");
          onChanged();
        })}>{t.workspaceDetail.step1CreateSite}</button>
      </div>
      {siteId && (
        <>
          <div className="row" style={{ marginTop: 10 }}>
            <input placeholder="hostname (e.g. localhost / www.example.com)"
                   value={hostname} onChange={(e) => setHostname(e.target.value)} />
            <button onClick={() => call(async () => {
              const r = await post<DomainChallenge>(
                `/api/v1/sites/${siteId}/domains`, { hostname });
              setChallenge(r); setMsg("DNS challenge issued");
            })}>{t.workspaceDetail.step2RequestDns}</button>
            <button onClick={() => call(async () => {
              await post(`/api/v1/sites/${siteId}/domains/verify`, { hostname });
              setMsg("Domain verified & site activated");
              onChanged();
            })}>{t.workspaceDetail.step3VerifyDns}</button>
          </div>
          {challenge && (
            <div style={{ marginTop: 8, fontSize: 13, background: "#f8fafc",
                         padding: 10, borderRadius: 8 }}>
              {t.workspaceDetail.txtRecordName}<code>{challenge.txt_name}</code><br />
              {t.workspaceDetail.txtRecordValue}<code>{challenge.txt_value}</code><br />
              <span className="muted">
                {t.workspaceDetail.dnsTip}
              </span>
            </div>
          )}
          <div className="row" style={{ marginTop: 10 }}>
            <button onClick={() => call(async () => {
              const r = await post<{ public_site_key: string }>(
                `/api/v1/sites/${siteId}/sources`, { kind: "browser" });
              setPub(r.public_site_key); onChanged();
            })}>{t.workspaceDetail.step4EnableBrowser}</button>
            <button onClick={() => call(async () => {
              const r = await post<{ agent_key: string }>(
                `/api/v1/sites/${siteId}/sources`, { kind: "origin" });
              setAgent(r.agent_key); onChanged();
            })}>{t.workspaceDetail.step5EnableOrigin}</button>
          </div>
          {pub && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              {t.guide.siteKeyLabel}<code>{pub}</code>{" "}
              <a href={`/demo.html?site=${pub}`} target="_blank" rel="noreferrer">
                {t.workspaceDetail.openDemoPage}</a>
            </div>
          )}
          {agent && (
            <div style={{ marginTop: 8, fontSize: 13, background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid #cbd5e1" }}>
              <div style={{ marginBottom: 4 }}>
                <b>{t.workspaceDetail.agentKeyHint}</b><code>{agent}</code>
              </div>
              <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
                {locale === "zh" ? (
                  <>
                    <b>🖥️ Nginx 服务端旁路采集端点：</b> <code>https://widget.groweb.dev/collect/v1/origin</code><br />
                    请求头：<code>Authorization: Bearer {agent}</code><br />
                    <i>说明：配置 Nginx <code>log_format</code> 为 JSON 后通过 Vector / Fluent-Bit 实时转发，网页前端无需插入任何 JS 代码！进入站点详情可查看完整配置示例。</i>
                  </>
                ) : (
                  <>
                    <b>🖥️ Nginx Server Log Ingest Endpoint:</b> <code>https://widget.groweb.dev/collect/v1/origin</code><br />
                    Header: <code>Authorization: Bearer {agent}</code><br />
                    <i>Format Nginx <code>log_format</code> as JSON and forward via Vector / Fluent-Bit. 0 frontend code required! See full configs in Site Detail.</i>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {msg && <p className="muted" style={{ marginTop: 8 }}>{msg}</p>}
      {err && <p className="error">{err}</p>}
    </div>
  );
}
